import {
  TableCell,
  TableCellLayout,
  TableCellProps,
  TableCellLayoutProps,
  makeStyles,
  mergeClasses,
  tokens,
  TooltipProps,
  TableCellLayoutSlots,
  Button,
} from '@fluentui/react-components';
import { Link as FluentLink } from '@fluentui/react-components';
import getItemPathByItemType from '@modules/documents/helpers/getItemPathByItemType.helper';
import FileIcon from '@modules/items/components/FileIcon';
import { isProductFolder } from '@modules/items/helpers/items.helper';
import type { TItem } from '@modules/items/types/items.types';
import { AppLink } from '../AppLink';
import { ControlledTooltip } from '../ControlledTooltip';
import { buildHref } from '../../utils';
import { sanitizeSnippetWithEm } from '../../helpers';
import { CSSProperties, MouseEvent, ReactNode } from 'react';
import classNames from 'classnames';

const DEFAULT_TOOLTIP_THRESHOLD = 50;
const DEFAULT_TOOLTIP_RELATIONSHIP = 'description';
const DEFAULT_TOOLTIP_POSITIONING = 'above-start';

type TCustomTooltipProps = {
  threshold?: number; // show tooltip if text length > threshold
  positioning?: TooltipProps['positioning'];
};

type TPadding = 'none' | 'tight' | 'medium' | 'large';
type TOpenMode = 'autoByType' | 'newTab' | 'sameTab';

type TVariant =
  // Link to an item (auto builds href, renders FileIcon + optional location)
  | {
      kind: 'item';
      item: TItem;
      elem?: string;
      highlightText?: string;
    }
  // Arbitrary href + label text (no media unless you pass media prop)
  | { kind: 'link'; href: string; label: string }
  // Plain text with optional truncation & tooltip
  | { kind: 'text'; label: string }
  // Inner HTML content (not sanitized)
  | { kind: 'html'; html: string };

export type SmartTableCellProps = {
  /** The content kind to render */
  variant: TVariant;

  /** Wrap in a <TableCell> or render only the <TableCellLayout> (for cards, lists, etc.) */
  wrap?: 'table-cell' | 'layout-only';

  descriptionHtml?: string;

  cellProps?: TableCellProps;
  layoutProps?: Omit<TableCellLayoutProps, 'media' | 'description'>;

  padding?: TPadding; // default: 'tight'
  openMode?: TOpenMode; // default: 'autoByType' for item, 'newTab' for link
  linkBold?: boolean; // default: false; makes main text bold
  linkDanger?: boolean; // default: false; makes main text red
  linkMultiline?: boolean; // default: false; allows link to wrap into multiple lines
  linkDisabled?: boolean; // default: false
  linkAppearance?: 'subtle' | 'default'; // default: 'subtle'
  onLinkClick?: (e: MouseEvent) => void;
  forcePlainText?: boolean; // default: false; render as plain text, not link, even if href is present

  /** Tooltip behavior */
  tooltip?: TCustomTooltipProps;

  /** Additional elements: after Layout (e.g., action buttons) or below the main row */
  after?: ReactNode;
  stackedBelow?: ReactNode; // vertically below the main line inside TableCellLayout
};

const toPaddingStyle = (padding: TPadding): CSSProperties => {
  if (padding === 'none') return { paddingTop: 0, paddingBottom: 0 };
  if (padding === 'tight') return { paddingTop: 3, paddingBottom: 3 };
  if (padding === 'medium') return { paddingTop: 8, paddingBottom: 8 };
  if (padding === 'large') return { paddingTop: 16, paddingBottom: 16 };
  return {};
};

const useStyles = makeStyles({
  descriptionHtml: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',

    '& em': {
      color: tokens.colorBrandForeground1,
      fontWeight: 600,
    },
  },
  descriptionDisabled: {
    color: tokens.colorNeutralForegroundDisabled,
    '& em': {
      color: tokens.colorNeutralForegroundDisabled,
    },
  },
});

export const SmartTableCell = ({
  variant,
  wrap = 'table-cell',
  descriptionHtml,
  cellProps,
  layoutProps,
  padding = 'tight',
  openMode,
  linkBold = false,
  linkDanger = false,
  linkDisabled = false,
  linkMultiline = false,
  linkAppearance = 'subtle',
  onLinkClick,
  forcePlainText = false,
  tooltip,
  after,
  stackedBelow,
}: SmartTableCellProps) => {
  const classes = useStyles();

  // Inner HTML component with sanitized content
  const DangerousHtmlSpan = ({ rawHtml, className }: { rawHtml: string; className?: string }) => (
    <span
      className={mergeClasses(classes.descriptionHtml, className)}
      dangerouslySetInnerHTML={{ __html: sanitizeSnippetWithEm(rawHtml) }}
    />
  );

  // Resolve main text + href + target behavior
  let labelText: string | undefined;
  let href: string | undefined;
  let target: '_blank' | undefined;

  // Determine media+description defaults from variant.kind === 'item'
  let finalMedia: TableCellLayoutSlots['media'] | undefined;
  let finalDescription: TableCellLayoutSlots['description'] | undefined;

  if (variant.kind === 'item') {
    const { item, elem, highlightText } = variant;

    labelText = item.name;
    const itemPath =
      isProductFolder(item) && item.path.length === 0
        ? `/product/${item._id}`
        : `/${getItemPathByItemType(item.type)}/${item._id}`;
    href = buildHref(itemPath, { elem, highlightText });
    const mode: TOpenMode = openMode ?? 'autoByType';
    target =
      mode === 'newTab' ? '_blank' : mode === 'sameTab' ? undefined : item.type === 'FOLDER' ? undefined : '_blank';

    finalMedia = (
      <FileIcon
        type={item.type}
        path={item.path}
        isBlock={item.isBlock}
        isFrozen={item.isFrozen}
        isTemplate={item.isTemplate}
        folderType={item.folderType}
        mimetype={item.latestMimetype}
        name={item.name}
        id={item._id}
      />
    );

    if (descriptionHtml == null) {
      finalDescription = <span className="whitespace-nowrap text-ellipsis">{item.recentData?.location}</span>;
    }
  } else if (variant.kind === 'link') {
    labelText = variant.label;
    href = variant.href;
    const mode: TOpenMode = openMode ?? 'newTab';
    target = mode === 'newTab' ? '_blank' : mode === 'sameTab' ? undefined : '_blank';
  } else if (variant.kind === 'text') {
    labelText = variant.label;
  }

  // Tooltip logic
  const tooltipContent = variant.kind === 'html' ? sanitizeSnippetWithEm(variant.html) : labelText;
  const threshold = tooltip?.threshold ?? DEFAULT_TOOLTIP_THRESHOLD;
  const tooltipEnabled = (tooltipContent?.length ?? 0) > threshold;

  const linkClassName = classNames('!block !w-full !truncate', {
    '!font-semibold': linkBold,
    '!text-red-500': linkDanger,
    '!text-wrap !whitespace-normal': linkMultiline,
  });

  const plainTextClassName = classNames(
    linkClassName,
    '!justify-start !p-0 !text-left !font-normal !border-none !min-w-0'
  );

  const main = (
    <ControlledTooltip
      enable={tooltipEnabled}
      content={String(tooltipContent)}
      relationship={DEFAULT_TOOLTIP_RELATIONSHIP}
      positioning={tooltip?.positioning ?? DEFAULT_TOOLTIP_POSITIONING}
      withArrow
    >
      {(() => {
        if (variant.kind === 'html') {
          return <DangerousHtmlSpan rawHtml={variant.html} />;
        }

        if (href && !forcePlainText) {
          if (openMode === 'sameTab' && !onLinkClick && !linkDisabled) {
            return (
              <AppLink
                to={href}
                appearance={linkAppearance}
                className={linkClassName}
                onClick={(e) => e.stopPropagation()}
              >
                {labelText}
              </AppLink>
            );
          }
          return (
            <FluentLink
              href={href}
              target={target}
              as={!onLinkClick ? undefined : 'span'} // render as link unless a custom handler is passed (e.g. select folder)
              onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                e.stopPropagation(); // avoid row click handlers
                onLinkClick?.(e);
              }}
              disabled={linkDisabled}
              appearance={linkAppearance}
              className={linkClassName}
            >
              {labelText}
            </FluentLink>
          );
        }

        return (
          <Button appearance="outline" disabled={linkDisabled} className={plainTextClassName}>
            {labelText}
          </Button>
        ); // plain text (text/snippet)
      })()}
    </ControlledTooltip>
  );

  const layout = (
    <TableCellLayout
      {...layoutProps}
      truncate
      media={finalMedia}
      description={
        descriptionHtml ? (
          <DangerousHtmlSpan
            rawHtml={descriptionHtml}
            className={linkDisabled ? classes.descriptionDisabled : undefined}
          />
        ) : (
          finalDescription
        )
      }
      style={{
        ...(layoutProps?.style ?? {}),
        ...toPaddingStyle(padding),
        ...(after ? { paddingRight: 40 } : {}), // 40px padding right for the action button
      }}
    >
      {/* main row (link/text/html) */}
      {main}
      {/* stacked content below the main row */}
      {stackedBelow ? <div className="flex flex-col -space-y-0.5">{stackedBelow}</div> : null}
    </TableCellLayout>
  );

  if (wrap === 'layout-only') {
    return (
      <>
        {layout}
        {after}
      </>
    );
  }

  return (
    <TableCell {...cellProps}>
      {layout}
      {after}
    </TableCell>
  );
};
